/**
 * [C35] Documents in the portal (B5 part 2; D-146 – D-150):
 *  - `usePrintSettings()` — show / hide profit on customer documents (D-148), the voucher format defaults (D-149).
 *  - `usePrintJob()` — the browser print of one template (A4 or 80 mm) through `printDocument`, the portal and the
 *    `afterprint` clean-up every container used to repeat.
 *  - `useDocuments()` — the server PDF of the same template and props (`POST /documents/pdf`, D-146), delivered as a
 *    download or through the device share sheet; words from the server (`POST /documents/words`, D-148).
 *  - `whatsappLink()` — the free link (D-063) to the account's stored phone in international form (D-150, F-221).
 * The section's own print route is always called first (number, copy, print audit, D-147); the PDF call only renders.
 */
import { createElement, useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { PRINT_ROOT_CLASS, printDocument } from "@faylasuf/print";
import { whatsappPhone, type DocumentPdfRequest, type VoucherFormats, type VoucherType } from "@faylasuf/contracts";
import type { PrintSettings } from "../api/types.js";
import { useApp, useSession } from "./context.js";
import { errorMessage, isSessionLost } from "./errors.js";
import { useToast } from "./toast.js";

const DEFAULT_FORMATS: VoucherFormats = { customer_entry: "a4", transfer_voucher: "a4", installment_receipt: "a4", sales_invoice: "a4" };

export function usePrintSettings(): PrintSettings {
  const { session } = useSession();
  return session.print ?? { showProfit: true, voucherFormats: DEFAULT_FORMATS, logo: false };
}

/**
 * The office default of one voucher type (D-149), switchable per copy by the caller's state. [C36] D09 T-18: the switch
 * is for **that copy** — `issued()` (called once a print / PDF of the copy has started) puts the window back on the
 * office default, so the next print uses the default again.
 */
export function useVoucherFormat(type: VoucherType): ["a4" | "80mm", (f: "a4" | "80mm") => void, () => void] {
  const def = usePrintSettings().voucherFormats[type] ?? "a4";
  const [format, setFormat] = useState<"a4" | "80mm">(def);
  useEffect(() => setFormat(def), [def]); // [C35 review 9] a changed office default reaches an open window
  return [format, setFormat, () => setFormat(def)];
}

/**
 * [C35 review 9] A tab opened at the click itself (popup blockers allow a window only inside the user's gesture, not
 * after an `await`): `go(url)` sends it to the link once the issue is recorded, `cancel()` closes it on failure.
 */
export function pendingTab(): { go: (url: string) => void; cancel: () => void } {
  const w = window.open("about:blank", "_blank");
  if (w) w.opener = null;
  return {
    go: (url) => {
      if (w && !w.closed) w.location.replace(url);
      else window.open(url, "_blank", "noopener");
    },
    cancel: () => w?.close(),
  };
}

/** True when this browser can hand a PDF file to the device share sheet. */
export function canShareFiles(): boolean {
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  try {
    return typeof nav.share === "function" && typeof nav.canShare === "function" && nav.canShare({ files: [new File([""], "x.pdf", { type: "application/pdf" })] });
  } catch {
    return false;
  }
}

/** D-150: `https://wa.me/<9647…>?text=…` (no recipient when the account has no usable phone). */
export function whatsappLink(phone: string | null | undefined, text: string): string {
  const p = whatsappPhone(phone);
  return `https://wa.me/${p ?? ""}?text=${encodeURIComponent(text)}`;
}

interface Job {
  readonly node: ReactNode;
  readonly id: string;
  readonly format: "a4" | "80mm";
}

/** One browser print at a time: `start(node, id, format)` mounts the template under the print root and prints it. */
export function usePrintJob(onDone?: () => void): { start: (node: ReactNode, id: string, format?: "a4" | "80mm") => void; portal: ReactNode; busy: boolean } {
  const [job, setJob] = useState<Job | null>(null);
  useEffect(() => {
    if (!job) return;
    const done = () => {
      setJob(null);
      onDone?.();
    };
    window.addEventListener("afterprint", done, { once: true });
    const h = setTimeout(() => printDocument(job.id, { format: job.format }), 80);
    return () => {
      clearTimeout(h);
      window.removeEventListener("afterprint", done);
    };
  }, [job]);
  return {
    start: (node, id, format = "a4") => setJob({ node, id, format }),
    portal: job ? createPortal(createElement("div", { className: PRINT_ROOT_CLASS, "data-testid": "print-root" }, job.node), document.body) : null,
    busy: job !== null,
  };
}

function download(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export function useDocuments() {
  const { source, t, locale } = useApp();
  const { sessionLost } = useSession();
  const toast = useToast();
  const fail = (e: unknown) => (isSessionLost(e) ? sessionLost() : toast(errorMessage(t, e), "error"));
  const fileOf = (req: DocumentPdfRequest) => `${req.fileName ?? req.template}.pdf`;

  return {
    /** D-146 server PDF → download. Returns false when it failed (the toast already says why). */
    async downloadPdf(req: DocumentPdfRequest): Promise<boolean> {
      try {
        download(await source.documentPdf(req), fileOf(req));
        toast(t("print.share.downloaded"));
        return true;
      } catch (e) {
        fail(e);
        return false;
      }
    },
    /**
     * D-150: the PDF through the device share sheet (phones, Safari, Edge); where a browser cannot share files, the PDF
     * is downloaded and, when a WhatsApp text is given, the chat opens with a clear prompt to attach the file.
     */
    async sharePdf(req: DocumentPdfRequest, whatsapp?: { phone: string | null; text: string }, opened?: ReturnType<typeof pendingTab> | null): Promise<boolean> {
      const share = canShareFiles();
      // the chat tab is opened at the click (popup blockers), only where the share sheet cannot take the file; a caller
      // that awaited something first passes the tab it opened at the click
      const tab = opened ?? (!share && whatsapp ? pendingTab() : null);
      let blob: Blob;
      try {
        blob = await source.documentPdf(req);
      } catch (e) {
        tab?.cancel();
        fail(e);
        return false;
      }
      const file = new File([blob], fileOf(req), { type: "application/pdf" });
      if (share) {
        try {
          await navigator.share({ files: [file], ...(whatsapp ? { text: whatsapp.text } : {}) });
          return true;
        } catch (e) {
          if ((e as { name?: string }).name === "AbortError") return false;
        }
      }
      download(blob, fileOf(req));
      if (whatsapp) {
        (tab ?? { go: (u: string) => window.open(u, "_blank", "noopener") }).go(whatsappLink(whatsapp.phone, whatsapp.text));
        toast(t("print.share.download_prompt"));
      } else toast(t("print.share.downloaded"));
      return true;
    },
    /** D-148: words of amounts from the server, one phrase per item; "" items on failure (the print goes on). */
    async words(items: Array<{ lines: Array<{ amount: string; currency: string }>; side?: "lakum" | "alaykum" | null }>): Promise<string[]> {
      if (items.length === 0) return [];
      try {
        return (await source.documentWords({ locale, items })).words;
      } catch {
        return items.map(() => "");
      }
    },
  };
}
